/**
 * What an author reads when a video did not make it, in the `video` namespace's words. Each branches
 * on a machine code - the media worker's `failureCode`, the API's `error_code` - never on prose,
 * and falls through to the cabinet's `describeProblem` for everything that is not about the video.
 */
import { ApiError, TransportError, UploadStalled } from '../../features/application/api/problem';
import {
  STREAM_ERROR_CODE,
  VIDEO_ERROR_CODE,
  VIDEO_FAILURE_CODES,
} from '../../features/cabinet/api/editor-api';
import type { VideoFailureCode } from '../../features/cabinet/api/editor-api';
import { describeProblem, hasErrorCode, isApiProblem } from '../../features/cabinet/api/problem';
import { CABINET_LOCALE } from '../../features/cabinet/format';
import { VideoFileRefused } from './use-video-upload';
import type { VideoT } from './video-i18n';

function isFailureCode(code: string): code is VideoFailureCode {
  return (VIDEO_FAILURE_CODES as readonly string[]).includes(code);
}

/** Why the media worker refused the file, from `ArticleVideo.failureCode`. */
export function describeVideoFailure(code: string | null | undefined, t: VideoT): string {
  return code !== null && code !== undefined && isFailureCode(code)
    ? t(`failure.${code}`)
    : t('failure.other');
}

/**
 * Why an upload did not go in: refused here, by Storage, or by `setArticleVideo`. A file refused
 * here for its length or its picture is said in the media worker's words for the same code.
 */
export function describeVideoUploadFailure(error: unknown, t: VideoT): string {
  if (error instanceof VideoFileRefused) {
    switch (error.reason) {
      case 'type':
      case 'size':
      case 'empty':
        return t(`upload.refused.${error.reason}`);
      default:
        return t(`failure.${error.reason}`);
    }
  }
  if (hasErrorCode(error, 'UPLOAD_TOO_LARGE')) {
    return t('upload.refused.size');
  }
  // `setArticleVideo`'s other 422 for a file: the piece is not a video (another tab, a new address).
  if (isApiProblem(error) && error.status === 422) {
    return t('editor.notAVideo');
  }
  if (error instanceof UploadStalled) {
    return t('upload.stalled');
  }
  if (error instanceof TransportError) {
    return t('upload.transport');
  }
  // Storage's own refusal is an `ApiError` with a status and no code: 401 is a lost sign-in, and
  // anything else is the rules saying no to this file.
  if (error instanceof ApiError) {
    return error.status === 401 ? t('upload.signedOut') : t('upload.storageRefused');
  }
  return describeProblem(error, 'video');
}

/** Why a YouTube link was not taken: the server's 422 is "not a link to one YouTube video". */
export function describeYoutubeFailure(error: unknown, t: VideoT): string {
  if (isApiProblem(error) && error.status === 422) {
    return t('youtube.notALink');
  }
  return describeProblem(error, 'video');
}

/**
 * Why a frame or the YouTube still did not become the cover. `setVideoCover`'s 422 is "this video
 * has no such frame" or "this video is not a YouTube link": the video changed after the page showed
 * what to choose from.
 */
export function describeCoverFailure(error: unknown, t: VideoT): string {
  if (isApiProblem(error) && error.status === 422) {
    return t('cover.refused');
  }
  return describeProblem(error, 'video');
}

/**
 * The refusals only a video's submit meets - and a live stream's two (1.2.0); anything else is the
 * caller's to word.
 */
export function videoSubmitRefusal(error: unknown, t: VideoT): string | null {
  if (hasErrorCode(error, VIDEO_ERROR_CODE.accessRequired)) {
    return t('refusal.accessRequired');
  }
  if (hasErrorCode(error, STREAM_ERROR_CODE.accessRequired)) {
    return t('refusal.streamAccessRequired');
  }
  if (hasErrorCode(error, STREAM_ERROR_CODE.timesRequired)) {
    return t('refusal.streamTimesRequired');
  }
  if (hasErrorCode(error, VIDEO_ERROR_CODE.videoRequired)) {
    return t('refusal.videoRequired');
  }
  if (hasErrorCode(error, VIDEO_ERROR_CODE.confirmationsRequired)) {
    return t('refusal.confirmationsRequired');
  }
  return null;
}

/** "4:12", "1:02:05" - a length as a player shows it. */
export function formatDuration(durationMs: number): string {
  const total = Math.max(0, Math.round(durationMs / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return hours > 0
    ? `${String(hours)}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${String(minutes)}:${seconds}`;
}

/** "240 MB", "2.4 GB" - in the cabinet's language, with its own unit words. */
export function formatSize(bytes: number): string {
  const megabytes = bytes / (1024 * 1024);
  if (megabytes < 1024) {
    return new Intl.NumberFormat(CABINET_LOCALE, {
      style: 'unit',
      unit: 'megabyte',
      maximumFractionDigits: megabytes < 10 ? 1 : 0,
    }).format(megabytes);
  }
  return new Intl.NumberFormat(CABINET_LOCALE, {
    style: 'unit',
    unit: 'gigabyte',
    maximumFractionDigits: 1,
  }).format(megabytes / 1024);
}

/** "1080p": a video's short side, as video sizes are named. */
export function formatResolution(widthPx: number, heightPx: number): string {
  return `${String(Math.min(widthPx, heightPx))}p`;
}
